import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("checking"); // checking | authed | anon

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data.user);
      setStatus("authed");
      return data.user;
    } catch {
      setUser(null);
      setStatus("anon");
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data.user);
    setStatus("authed");
    return data.user;
  };

  const forgotPassword = async (email) => {
    const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
    return data;
  };

  const resetPassword = async (token, password) => {
    const { data } = await axios.post(`${API}/auth/reset-password`, { token, password });
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
    setUser(data.user);
    setStatus("authed");
    return data.user;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    setUser(null);
    setStatus("anon");
  };

  const upgrade = async () => {
    const { data } = await axios.post(`${API}/subscription/upgrade`);
    setUser(data.user);
    return data.user;
  };

  const startCheckout = async (lookupKey) => {
    const origin = window.location.origin;
    const { data } = await axios.post(`${API}/payments/checkout`, {
      lookup_key: lookupKey,
      origin_url: origin,
    });
    window.location.href = data.checkout_url;
  };

  const openPortal = async () => {
    const { data } = await axios.post(`${API}/payments/portal`, {
      return_url: window.location.origin,
    });
    window.location.href = data.portal_url;
  };

  const downgrade = async () => {
    const { data } = await axios.post(`${API}/subscription/downgrade`);
    setUser(data.user);
    return data.user;
  };

  return (
    <AuthContext.Provider value={{ user, status, login, register, logout, refresh, upgrade, downgrade, startCheckout, openPortal, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
